inherit("App", {
	parent: `Base`,
	singleton: true,
	requires: [`DateTimeLocaleObserver`, `Insights`],
	defaults: {
		theme: `DARK`,
	},
	prepare(setup) {
		const { data, Templates } = this;
		const firstPartyCookie = this.getFirstPartyCookie();
		const { userId: savedUserId, userEmailHash: savedUserEmailHash } = firstPartyCookie;
		const cookieKey = `tixr-user-history`;
		const cookieExpiration = 365;

		this.assign(data, { cookieKey, cookieExpiration, savedUserId, savedUserEmailHash });

		Templates.setIconMode(`font`);
		this.registerPaths();

		// NOTE: groupId and eventId are obtained with the "page requirements" call, don't need to calculate
	},

	constructor(setup) {
		const params = this.getHashParams();
		const { access_token: accessToken, graph_domain: method } = params;

		if (method === `facebook` && this.isString(accessToken, 1)) {
			// looks like a FB redirect login
			// NOTE: don't return, avoid circular dependencies
			this.require({
				deps: [`User`],
				then: () => this.User.logIn({ accessToken, method }),
			});
		}
	},

	ready(setup) {
		this.DateTimeLocaleObserver.observe(); // start observing for locale time changes

		// Workaround to prevent displaying banner in iOS and Android apps
		this.defer({
			duration: 3000,
			then: () => {
				this.determineCookieConsentVisibility();
			},
		});
	},

	api: {
		determineCookieConsentVisibility: {
			async value(context, options) {
				const { Geolocation, Insights, System } = this;
				const system = System.get();
				const { isNativeApp } = system;
				const agentHandle = this.getCookie(`agentHandle`);
				const isAgentMode = this.isString(agentHandle, 1);

				if (!isNativeApp && !isAgentMode) {
					const isTrue = `1`;
					const isAllowFunctionalCookies = this.getCookie(`allow_functional_cookies`) === isTrue;
					const isAllowAnalyticsCookies = this.getCookie(`allow_analytics_cookies`) === isTrue;
					const isAllowMarketingCookies = this.getCookie(`allow_marketing_cookies`) === isTrue;
					const geo = await Geolocation.get();
					const isShowCookieConsentBanner = this.getCookie(`cookie_warning_shown`) !== isTrue && !geo.in_us;

					if (isShowCookieConsentBanner) {
						this.create({
							namespace: `Modules/CookieConsentBanner`,
							setup: {
								container: this.get(`#cookie-consent-banner`, document),
							},
						});
					}
					if (this.getCookie(`cookie_warning_shown`) !== isTrue && geo.in_us) {
						// user is in the United States and hasn't specified cookie preferences yet, set all to true
						Insights.setAllowCapture({
							allowFunctional: true,
							allowAnalytics: true,
							allowMarketing: true,
						});

						// save cookies for a year
						this.setCookie(`allow_functional_cookies`, `1`, 365);
						this.setCookie(`allow_analytics_cookies`, `1`, 365);
						this.setCookie(`allow_marketing_cookies`, `1`, 365);
					} else {
						Insights.setAllowCapture({
							allowFunctional: isAllowFunctionalCookies,
							allowAnalytics: isAllowAnalyticsCookies,
							allowMarketing: isAllowMarketingCookies,
						});
					}
				}
			},
		},
		setFirstPartyCookie: {
			public: true,
			value(context, options) {
				const { userEmailHash, userId } = options;
				const { data } = this;
				const { cookieExpiration, cookieKey } = data;

				if (!this.isString(userEmailHash, 1) || !this.isString(userId, 1)) {
					throw new Error(`Missing required userEmailHash and userId.`);
				}

				this.assign(data, { savedUserEmailHash: userEmailHash, savedUserId: userId });

				const value = { userEmailHash, userId };
				this.setCookie(cookieKey, JSON.stringify(value), { expires: cookieExpiration });
			},
		},
		clearFirstPartyCookie: {
			public: true,
			value(context, options) {
				const { data } = this;
				const { cookieKey } = data;

				this.assign(data, { savedUserEmailHash: null, savedUserId: null });
				this.deleteCookie(cookieKey);
			},
		},
		getFirstPartyCookie: {
			public: true,
			value(context, options) {
				const { data } = this;
				const { cookieKey } = data;
				const firstPartyCookieString = this.getCookie(cookieKey);

				try {
					if (!this.isString(firstPartyCookieString, 1)) {
						return {};
					}

					return JSON.parse(firstPartyCookieString);
				} catch (error) {
					return {};
				}
			},
		},

		registerPaths: {
			value() {
				const { handlePath } = this;

				// Landing Page
				handlePath({
					paths: [`/`, `/landing`],
					handler: (request, response) => {
						response.page = `Landing`;
						// response.page = `NewPassword`;
						// response.page = `ViewTransfer`;
						// response.setup = {
						// 	email: `tyler.mccreary+test@tixr.com`,
						// 	offerId: `F1D81QF`,
						// 	requiresShippingAddress: true,
						// 	// portraitBackgroundImageUrl: `https://static.test.tixr.com/static/images/external/img/03edb99a-3db0-4cd5-8d93-c208bcef5861.png`,
						// 	// landscapeBackgroundImageUrl: `https://static.test.tixr.com/static/images/external/img/a3729b36-0cb2-42eb-b082-e8c0c608fa87.png`,
						// };
					},
				});

				// Groups Page
				handlePath({
					path: `/groups`,
					handler: (request, response) => {
						response.page = `Groups`;
					},
				});

				// Group Page
				handlePath({
					path: `/groups/*`,
					handler: (request, response) => {
						response.page = `Group`;
						// NOTE: groupId passed as part of the "page requirements call"
					},
				});

				// Group Page
				handlePath({
					path: `/groups/*/sales/*`,
					handler: (request, response) => {
						response.page = `GroupSales`;
					},
				});

				// Promoter Page
				handlePath({
					paths: [`/promoters/*`, `/concierge/*`],
					handler: ({ slugs }, response) => {
						const handle = slugs[1];

						response.page = `Promoter`;
						response.setup = { handle };
					},
				});

				// Agent Page
				handlePath({
					path: `/agents/*`,
					handler: ({ slugs }, response) => {
						const handle = slugs[1];

						response.page = `Agent`;
						response.setup = { handle };
					},
				});

				// Event Page
				handlePath({
					path: `/groups/*/events/*`,
					handler: (request, response) => {
						response.page = `Event`;
					},
				});

				// Video Page
				handlePath({
					paths: [`/groups/*/events/*/videos/*`, `/groups/*/events/*/videochat/*`],
					handler: (request, response) => {
						response.page = `Video`;
					},
				});

				// User Home
				handlePath({
					paths: [`/home`, `/home/*`],
					handler: (request, response) => {
						const { slugs } = request;
						const section = slugs[1];

						response.page = `Home`;
						response.setup = { section };
					},
				});

				// About Page
				handlePath({
					path: `/about`,
					handler: (request, response) => {
						response.page = `Static`;
						response.setup = { page: `about` };
					},
				});

				// Privacy Policy
				handlePath({
					path: `/privacy`,
					handler: (request, response) => {
						response.page = `Static`;
						response.setup = { page: `privacy` };
					},
				});

				// Cookie Policy
				handlePath({
					path: `/cookie-policy`,
					handler: (request, response) => {
						response.page = `Static`;
						response.setup = { page: `cookie-policy` };
					},
				});

				// Terms of Service
				handlePath({
					path: `/termsofservice`,
					handler: (request, response) => {
						response.page = `Static`;
						response.setup = { page: `termsofservice` };
					},
				});

				// Seller terms
				handlePath({
					path: `/seller-terms`,
					handler: (request, response) => {
						response.page = `Static`;
						response.setup = { page: `seller-terms` };
					},
				});

				// Purchase Policy
				handlePath({
					path: `/purchase-policy`,
					handler: (request, response) => {
						response.page = `Static`;
						response.setup = { page: `purchase-policy` };
					},
				});

				// Waitlist
				handlePath({
					paths: [`/waitlist`, `/waitlist/*`],
					handler: (request, response) => {
						const { slugs } = request;
						const key = slugs[1];
						let page = `waitlist`;

						if (this.isString(key, 1)) {
							page += `-${key}`;
						}

						response.page = `Static`;
						response.setup = { page };
					},
				});

				// Rewards
				handlePath({
					paths: [`/rewards`, `/rewards/*`],
					handler: (request, response) => {
						const { slugs } = request;
						const key = slugs[1];
						let page = `rewards`;

						if (this.isString(key, 1)) {
							page += `-${key}`;
						}

						response.page = `Static`;
						response.setup = { page };
					},
				});

				// paths that go to JsShell
				handlePath({
					paths: [`/printinvoice`],
					handler: (request, response) => {
						response.page = `JsShell`;
						response.setup = { path: request.path };
					},
				});

				// G.D.P.R.
				handlePath({
					path: `/gdpr`,
					handler: (request, response) => {
						response.page = `Static`;
						response.setup = { page: `gdpr` };
					},
				});

				// New Password (from "forgot password" email link)
				handlePath({
					path: `/passwd`,
					handler: (request, response) => {
						response.page = `NewPassword`;
					},
				});

				// Device activation
				handlePath({
					path: `/activate`,
					handler: (request, response) => {
						response.page = `Activate`;
					},
				});

				// Do not sell my info privacy form
				this.handlePath({
					path: `/do-not-sell-my-info`,
					handler: (request, response) => {
						response.page = `DoNotSellMyInfo`;
					},
				});

				// Privacy choices
				this.handlePath({
					path: `/privacy-choices`,
					handler: (request, response) => {
						response.page = `PrivacyChoices`;
					},
				});

				// Error
				handlePath({
					path: `/error`,
					handler: (request, response) => {
						response.page = `Static`;
						response.setup = { page: `error` };
					},
				});

				// OAuth Login
				handlePath({
					path: `/oauth`,
					handler: (request, response) => {
						response.page = `OAuth`;
					},
				});

				// User Log Out
				handlePath({
					path: `/logout`,
					handler: (request, response) => {
						let promise;

						promise = this.require({ deps: [`User`] });

						promise = this.when({
							promise,
							then: () => {
								const { User } = this;

								User.unsubscribe(); // ignore whether the user remains signed in
								return User.logOut();
							},
						});

						return this.when({
							promise,
							then: () => {
								response.href = `/`;
							},
						});
					},
				});

				// External pages
				handlePath({
					paths: [`/blog`, `/pr`],
					handler: (request, response) => {
						// intentionally ignore these cases, to fail into a full page load
						response.page = null;
					},
				});

				handlePath({
					path: `/*`,
					handler: (request, response) => {
						// TEMPORARY, until vanity URLs are solved
						response.page = `Group`;
					},
				});
			},
		},

		/**
		 * Used to capture insights about the user behavior, converting from Tixr's schema to the more generic Insights one.
		 * @method App#captureInsight
		 */
		captureInsight: {
			public: true,
			async value(context, options) {
				const { eventType } = options;
				const deps = [`Events`, `Groups`, `Insights`, `ShoppingCart`, `User`];
				await this.require({ deps });
				const { data, Events, Groups, Insights, ShoppingCart, User } = this;
				const { savedUserEmailHash: userEmailHash, savedUserId: tixr_id } = data;
				const custom = {};
				const simpleCaptureDetailsFlows = [`Payment Plan`, `Order Protection`];
				let user;

				try {
					user = await User.get({ force: false });
				} catch (error) {
					user = null;
					// swallow error
				}

				if (this.isString(userEmailHash)) {
					custom.email = userEmailHash;
				}
				if (this.isString(tixr_id)) {
					custom.tixr_id = tixr_id;
				}

				const captureDetails = {
					// values from the First Party Cookie
					custom,
					eventType,
					user: this.makeInsightObject({ type: `user`, user }),
				};

				switch (eventType) {
					case `cart-add`:
					case `cart-remove`: {
						const { item, productId, productType, selections, purchaseFlow, purchaseFlowItem } = options;

						// Adding/removing order protection or payment plan to the cart triggers
						// a cart-add/cart-remove event, but it passes item { id, name, price }
						// in options instead of assigning below via this.makeInsightObject()
						if (this.includes(simpleCaptureDetailsFlows, purchaseFlowItem)) {
							if (this.isObject(item)) {
								this.assign(captureDetails, { item });
							} else {
								// fall back in case item is not passed
								this.assign(captureDetails, { item: { id: null, name: null, price: 0 } });
							}

							this.assign(captureDetails, {
								productList: {},
								purchaseFlowItem,
							});
						} else {
							const [selection] = selections;
							const { holdId, holdName, priceLevelOptionId, priceLevelOptionName } = selection;
							const cart = await ShoppingCart.get();
							const { eventId, products } = cart;
							const product = this.find(products, { id: productId, productType });
							const event = await Events.get({ id: eventId });
							const { group } = event;

							this.assign(captureDetails, {
								brand: this.makeInsightObject({ type: `brand`, group }),
								cart: this.makeInsightObject({ type: `cart`, cart, event }),
								location: this.makeInsightObject({ type: `location`, event }),
								item: this.makeInsightObject({ type: `item`, product, products, selections }),
								performers: this.makeInsightObject({ type: `performers`, event }),
								productList: this.makeInsightObject({ type: `productList`, event }),
								purchaseFlow,
								purchaseFlowItem,
							});
							this.assign(custom, { holdId, holdName, priceLevelOptionId, priceLevelOptionName });
						}
						break;
					}
					case `cart-view`:
					case `checkout-init`: {
						const cart = await ShoppingCart.get();
						const { eventId } = cart;
						const paymentPlanFee = this.isObject(cart) && cart.usePaymentPlan ? cart.paymentPlanFee : null;
						const orderProtectionFee =
							this.isObject(cart) && cart.orderProtectionAdded ? cart.orderProtection.quote : null;
						const event = await Events.get({ id: eventId });
						const { group } = event;
						const { purchaseFlow } = options;

						this.assign(captureDetails, {
							brand: this.makeInsightObject({ type: `brand`, group }),
							cart: this.makeInsightObject({ type: `cart`, cart, event }),
							location: this.makeInsightObject({ type: `location`, event }),
							performers: this.makeInsightObject({ type: `performers`, event }),
							productList: this.makeInsightObject({ type: `productList`, event }),
							purchaseFlow,
							orderProtectionFee,
							paymentPlanFee,
						});
						break;
					}
					case `checkout-step-complete`:
					case `checkout-step`: {
						const { step, purchaseFlow } = options;
						const cart = await ShoppingCart.get();
						const { eventId } = cart;
						const paymentPlanFee = this.isObject(cart) && cart.usePaymentPlan ? cart.paymentPlanFee : null;
						const orderProtectionFee =
							this.isObject(cart) && cart.orderProtectionAdded ? cart.orderProtection.quote : null;
						const event = await Events.get({ id: eventId });
						const { group } = event;

						this.assign(captureDetails, {
							brand: this.makeInsightObject({ type: `brand`, group }),
							cart: this.makeInsightObject({ type: `cart`, cart, event }),
							location: this.makeInsightObject({ type: `location`, event }),
							performers: this.makeInsightObject({ type: `performers`, event }),
							productList: this.makeInsightObject({ type: `productList`, event }),
							step,
							purchaseFlow,
							orderProtectionFee,
							paymentPlanFee,
						});
						break;
					}
					case `product-list-view`: {
						const { eventId } = options;
						const event = await Events.get({ id: eventId });
						const { group } = event;

						this.assign(captureDetails, {
							brand: this.makeInsightObject({ type: `brand`, group }),
							location: this.makeInsightObject({ type: `location`, event }),
							performers: this.makeInsightObject({ type: `performers`, event }),
							productList: this.makeInsightObject({ type: `productList`, event }),
						});
						break;
					}
					case `page-view`: {
						const {
							cart,
							eventId,
							groupId,
							pageType,
							path,
							paymentPlanFee,
							purchaseFlow,
							purchaseFlowItem,
							title,
							orderProtectionFee,
						} = options;

						if (this.isInt(eventId, 1)) {
							// Event comes bundled with group
							const event = await Events.get({ id: eventId });
							const { group } = event;
							const productList = this.makeInsightObject({ event, type: `productList` });
							const brand = this.makeInsightObject({ group, type: `brand` });

							this.assign(captureDetails, { brand, productList });
						} else if (this.isInt(groupId, 1)) {
							// Group-only
							const group = await Groups.get({ id: groupId });
							const brand = this.makeInsightObject({ group, type: `brand` });

							captureDetails.brand = brand;
						}

						this.assign(captureDetails, {
							cart,
							pageType,
							path,
							paymentPlanFee,
							purchaseFlow,
							purchaseFlowItem,
							title,
							orderProtectionFee,
						});
						break;
					}
					case `product-click`:
					case `product-view`: {
						const { saleId } = options;
						const cart = await ShoppingCart.get();
						const { eventId, products } = cart;
						const event = await Events.get({ id: eventId });
						const { group } = event;
						const productType = `SALE`;
						const sale = this.find(products, { id: saleId, productType });

						this.assign(captureDetails, {
							brand: this.makeInsightObject({ type: `brand`, group }),
							location: this.makeInsightObject({ type: `location`, event }),
							performers: this.makeInsightObject({ type: `performers`, event }),
							product: this.makeInsightObject({ type: `product`, sale }),
							productList: this.makeInsightObject({ type: `productList`, event }),
						});
						break;
					}
					case `purchase`: {
						const { purchaseFlow } = options;
						const cart = await ShoppingCart.get();
						const { eventId } = cart;
						const paymentPlanFee = this.isObject(cart) && cart.usePaymentPlan ? cart.paymentPlanFee : null;
						const orderProtectionFee =
							this.isObject(cart) && cart.orderProtectionAdded ? cart.orderProtection.quote : null;
						const event = await Events.get({ id: eventId });
						const { group } = event;

						this.assign(captureDetails, {
							brand: this.makeInsightObject({ type: `brand`, group }),
							cart: this.makeInsightObject({ type: `cart`, cart, event }),
							location: this.makeInsightObject({ type: `location`, event }),
							order: this.makeInsightObject({ type: `order`, cart }),
							performers: this.makeInsightObject({ type: `performers`, event }),
							productList: this.makeInsightObject({ type: `productList`, event }),
							purchaseFlow,
							paymentPlanFee,
							orderProtectionFee,
						});
						break;
					}
					case `user-log-in`:
					case `user-sign-up`: {
						const { method } = options;

						this.assign(captureDetails, { method });
						break;
					}
					case `social-share`: {
						const { eventId, groupId, method } = options;

						if (this.isInt(eventId, 1)) {
							const event = await Events.get({ id: eventId });
							const { group } = event;
							const brand = this.makeInsightObject({ type: `brand`, group });
							const productList = this.makeInsightObject({ type: `productList`, event });
							const location = this.makeInsightObject({ type: `location`, event });
							const performers = this.makeInsightObject({ type: `performers`, event });
							const type = `productList`;

							this.assign(captureDetails, { brand, location, method, performers, productList, type });
						} else if (this.isInt(groupId, 1)) {
							const group = await Groups.get({ id: groupId });
							const brand = this.makeInsightObject({ type: `brand`, group });
							const type = `brand`;

							this.assign(captureDetails, { brand, method, type });
						}
						break;
					}
					case `upgrade-load-form`:
					case `upgrade-see-available`:
					case `upgrade-exit-form`: {
						const { purchaseFlow } = options;

						this.assign(captureDetails, { purchaseFlow });
						break;
					}
					case `upgrade-deselect-original-ticket`:
					case `upgrade-select-original-ticket`: {
						const { brand, category, eventType, id, name, productType, purchaseFlow, quantity } = options;

						this.assign(captureDetails, {
							brand,
							category,
							eventType,
							id,
							name,
							productType,
							purchaseFlow,
							quantity,
						});
						break;
					}
					case `wishlist-add`:
					case `wishlist-remove`: {
						const { eventId } = options;
						const event = await Events.get({ id: eventId });
						const { group } = event;

						this.assign(captureDetails, {
							brand: this.makeInsightObject({ type: `brand`, group }),
							performers: this.makeInsightObject({ type: `performers`, event }),
							productList: this.makeInsightObject({ type: `productList`, event }),
							type: `event`,
						});
						break;
					}
				}

				Insights.capture(captureDetails);
			},
		},

		/**
		 * Used to convert different Tixr objects into what's expected by the Insights 3rd party tracking singleton.
		 * @method App#makeInsightObject
		 */
		makeInsightObject: {
			value(options) {
				const { Utils } = this;
				const { type } = options;

				switch (type) {
					case `brand`: {
						const { group } = options;
						const { name, trackingId: id } = group;
						const custom = {};

						return { custom, id, name };
					}
					case `cart`: {
						const { cart } = options;
						const { products, quantity, subtotal, total: cartTotal } = cart;
						const total = this.isFinite(cartTotal) ? cartTotal : subtotal;
						const items = [];
						this.each(products, (product) => {
							const { selections } = product;

							const groupedSelections = Utils.groupBy(selections, (selection) => {
								const { holdId, saleId, priceLevelOptionId } = selection;
								return `${holdId}_${saleId}_${priceLevelOptionId}`;
							});

							this.each(groupedSelections, (grouped) => {
								const item = this.makeInsightObject({ type: `item`, product, products, selections: grouped });
								items.push(item);
							});
						});

						return { items, quantity, total };
					}
					case `item`: {
						const { product, products, selections } = options;
						const { length: quantity } = selections;
						const [selection] = selections;
						const { holdId, holdName, name, price, priceLevelOptionId, priceLevelOptionName, saleId } = selection;
						const { category, productType, thumbUrl } = product;
						const custom = { holdId, holdName, priceLevelOptionId, priceLevelOptionName };
						const value = price * quantity;
						let saleStart;
						let saleEnd;
						let id;

						if (productType === `SALE`) {
							id = product.trackingId;
							saleEnd = product.endDate;
							saleStart = product.startDate;
						} else {
							const sale = this.find(products, { id: saleId, productType: `SALE` });
							id = sale.trackingId;
							saleEnd = sale.endDate;
							saleStart = sale.startDate;
						}

						return { category, custom, id, name, price, quantity, saleEnd, saleStart, thumbUrl, value };
					}
					case `location`: {
						const { event } = options;
						const { venue } = event;
						const { address, name } = venue;
						const custom = {};
						let addressString;

						if (this.isObject(address)) {
							const { city, countryCodeAlpha2, extendedAddress, postalCode, state, streetAddress } = address;
							const pieces = [streetAddress, extendedAddress, city, state, postalCode, countryCodeAlpha2];
							const validPieces = this.filter(pieces, (piece) => this.isString(piece, 1));

							if (validPieces.length > 0) {
								addressString = validPieces.join(`, `);
							}
						}

						if (addressString === undefined) {
							addressString = null;
						}

						return { address, addressString, custom, name };
					}
					case `order`: {
						const { cart } = options;
						const { orderId } = cart;

						return { id: orderId };
					}
					case `performers`: {
						const { event } = options;
						const { lineups } = event;
						const performers = [];

						this.each(lineups, ({ acts }) => {
							this.each(acts, ({ artist, type }) => {
								const { name, imageUrl } = artist;
								const isHeadliner = type === `HEADLINER`;
								const custom = {};
								const entry = { custom, isHeadliner, name };

								if (this.isString(imageUrl, 1)) {
									entry.imageUrl = imageUrl;
								}

								performers.push(entry);
							});
						});

						return performers;
					}
					case `product`: {
						const { sale } = options;
						const {
							advertisedPrice: price,
							category,
							name,
							endDate: saleEnd,
							startDate: saleStart,
							thumbUrl = null,
							trackingId: id,
						} = sale;
						const custom = {};

						return { category, custom, id, name, price, saleEnd, saleStart, thumbUrl };
					}
					case `productList`: {
						const { event } = options;
						const {
							eventCategoryName: category,
							currency,
							endDate,
							keywords,
							name,
							sales,
							saleWindowEnd: saleEnd,
							saleWindowStart: saleStart,
							startDate,
							trackingId: id,
						} = event;
						const products = this.map(sales, (sale) => this.makeInsightObject({ sale, type: `product` }));
						const custom = {};

						return {
							category,
							currency,
							custom,
							endDate,
							id,
							keywords,
							name,
							products,
							saleStart,
							saleEnd,
							startDate,
						};
					}
					case `user`: {
						const { user } = options;

						if (!this.isObject(user)) {
							// not logged in
							return null;
						}

						const { email, emailHash, firstName, gender, lastName, phoneNumber, postalCode, trackingId: id } = user;
						const custom = {};
						const birthday = this.clone(user.birthday);
						const birthdayString = this.isArray(birthday) ? birthday.reverse().join(`-`) : null;

						return {
							birthday: birthdayString,
							custom,
							email,
							emailHash,
							firstName,
							gender,
							id,
							lastName,
							phoneNumber,
							postalCode,
						};
					}
				}
			},
		},

		/**
		 * !!!!!!!! DON'T USE THIS METHOD !!!!!!!!
		 * This will not work in safari. The async functionality will cause the popup to be blocked
		 * TODO - rewrite this method to use a synchronous method to open the popup
		 */
		share: {
			async: true,
			public: true,
			async value(context, options) {
				const deps = [`Events`, `Groups`, `Insights`, `Social`, `User`];
				await this.require({ deps });
				const { Config, Events, Groups, Social, Time, User } = this;
				const { PRIMARY_DOMAIN } = Config;
				let user;

				try {
					user = await User.get({ force: false });
				} catch (error) {
					user = null;
					// swallow error
				}

				const { contentId, contentType, method } = options;
				const eventType = `social-share`;
				let message;
				let image;
				let url;

				switch (contentType) {
					case `event`: {
						const event = await Events.get({ id: contentId });
						const { flyerUrl, id: eventId, name: eventName, shortUrl, startDate, venue } = event;
						const { name: venueName, timezone, venueType } = venue;
						const formattedDate = Time.format(`%m %d`, startDate, timezone);

						if (this.isObject(user) && this.isString(user.amplifierUrl, 1)) {
							url = `${PRIMARY_DOMAIN}${user.amplifierUrl}${eventId}`;
						} else {
							url = shortUrl;
						}

						if (venueType === `VIRTUAL`) {
							message = `${eventName} on ${formattedDate}`;
						} else {
							message = `${eventName} at ${venueName} on ${formattedDate}`;
						}

						image = flyerUrl;

						this.captureInsight({ eventId, eventType, method });
						break;
					}
					case `group`: {
						const group = await Groups.get({ id: contentId });
						const { externalUrl, id: groupId, logoUrl, name } = group;

						// Twitter already has a "via Tixr_" on the end of the message
						message = method === `twitter` ? name : `${name} on Tixr`;
						url = externalUrl;
						image = logoUrl;

						this.captureInsight({ groupId, eventType, method });
						break;
					}
				}

				Social.share({ image, message, method, url });
			},
		},
	},
});

//# sourceMappingURL=App.js.map